import React, { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import {Box, Checkbox, FormControl, FormControlLabel, Grid, Input, MenuItem, Select, Typography} from "@mui/material";

import backgroundImage from '../../resources/Login/login-image.png';
import {registerSPARXUser} from "../../services/HttpService";
import { useAuth } from "../../services/AuthProvider";
import styles from './Register.module.css';
import CloseIcon from '@mui/icons-material/Close';
import ReCAPTCHA from 'react-google-recaptcha';

import {
  EmailPattern,
  getNewEthnicGroups,
  getPasswordRecommendations,
  getPasswordStrength, PasswordPattern,
  UsernamePattern
} from "../../services/Util";
import {ethnicGroupOptions, genderGroupOptions, howDidYouFindOutOptions, whoAreYouGroupOptions} from "../../services/Types";
import SpinnerModal from "../../components/Modal/Spinner";

const RECAPTCHA_SITE_KEY = '6LdYWGYbAAAAAJUL-6ZUrSNbJN48hFpdm4q8FSJq';

enum ErrorEnum {
  INVALID_USERNAME = 'Please enter a username.',
  INVALID_USERNAME_FORMAT = 'The username can only contain alphanumeric characters (A-Z, a-z and 0-9).',
  USERNAME_IN_USE = 'The username <em>{PLACEHOLDER}</em> is already taken. Please select a different one.',
  INVALID_AGE_GROUP = "Please select a valid 'Who are you?' answer.",
  INVALID_ETHNIC_GROUP = 'Please tell us your main ethnic group.',
  INVALID_OTHER_ETHNIC = 'Please tell us your main ethnic group (Other).',
  INVALID_GENDER = 'Please select a gender.',
  INVALID_EMAIL_FORMAT = 'Please enter a valid email address.',
  INVALID_CONFIRM_EMAIL = 'Email confirmation should match email address',
  EMAIL_IN_USE = 'The e-mail address <em>{PLACEHOLDER}</em> is already registered. Have you <a href="/forgot-password" style="color: #843534; font-weight: 500">forgotten your password</a>?',
  INVALID_PASSWORD = 'Password must be at least 8 characters.',
  INVALID_PASSWORD_FORMAT = 'Password must at have a number or symbol character.',
  INVALID_CONFIRM_PASSWORD = 'The specified passwords do not match.',
  INVALID_HOW_DID_YOU_FIND_OUT = 'Please choose how you found out about SPARX.',
  INVALID_OTHER_HOW_DID_YOU_FIND_OUT = 'Please enter how you found out about SPARX (Other).',
  INVALID_AGREEMENT_EXTRA_HELP = 'Please tick to confirm that you know you need to get extra help if SPARX is not helping you.',
  INVALID_AGREEMENT_TERMS = 'Please agree to the Terms and Conditions.',
  INVALID_CAPTCHA = 'Please check the CAPTCHA field.',
}

const initialErrorMessages = {
  username: '',
  ageGroup: '',
  ethnicGroups: '',
  ethnicGroupOther: '',
  gender: '',
  email: '',
  confirm_email: '',
  password: '',
  confirm_password: '',
  howDidYouFindOut: '',
  howDidYouFindOutOther: '',
  agreement_extra_help: '',
  agreement_terms: '',
  captcha: '',
}

const Register: React.FC = () => {
  const navigate = useNavigate();
  const { isLoggedIn, isIPBlocked } = useAuth();

  const [isBannerOpen, setIsBannerOpen] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [errorMessages, setErrorMessages] = useState(initialErrorMessages);
  const [backendErrorMessages, setBackendErrorMessages] = useState('');
  const [isFormTouchedAfterFailedSubmit, setIsFormTouchedAfterFailedSubmit] = useState(false);

  const [username, setUsername] = useState<string>('');
  const [ageGroup, setAgeGroup] = useState<string>('');
  const [ethnicGroups, setEthnicGroups] = useState<string[]>([]);
  const [ethnicGroupOther, setEthnicGroupOther] = useState<string>('');
  const [gender, setGender] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [confirmEmail, setConfirmEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [howDidYouFindOut, setHowDidYouFindOut] = useState<string>('');
  const [howDidYouFindOutOther, setHowDidYouFindOutOther] = useState<string>('');
  const [agreeHelp, setAgreeHelp] = useState<boolean>(false);
  const [agreeTermsConditions, setAgreeTermsConditions] = useState<boolean>(false);
  const [captchaValue, setCaptchaValue] = useState<string | null>(null);

  useEffect(() => {
    if (isLoggedIn) {
      navigate('/dashboard');
    }

    if (isIPBlocked) {
      setIsBannerOpen(true);
    }
  }, [isLoggedIn, navigate, isIPBlocked, setIsBannerOpen]);

  const handleEthnicCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValues = getNewEthnicGroups(event, ethnicGroups);
    setEthnicGroups(newValues);
  };

  // Password validation checks
  const passwordRecommendations = getPasswordRecommendations(password);
  const passwordStrength = getPasswordStrength(passwordRecommendations);

  const handleFormFocus = async (event: React.FocusEvent) => {
    const target = event.target as HTMLInputElement;

    if (target.type === 'submit') {
      await handleSubmit(event);
    }

    // Exclude submit button from triggering this logic
    if (target.type !== 'submit' && !isFormTouchedAfterFailedSubmit) {
      setIsFormTouchedAfterFailedSubmit(true);
      setIsBannerOpen(false);
      setErrorMessages(initialErrorMessages);
    }
  };

  // Return true if all conditions are met
  const checkFormValid = () : boolean => {
    const isUsernameValid = username.trim() !== '';
    const isUsernameFormatValid = UsernamePattern.test(username);
    const isAgeGroupValid = ageGroup.trim() !== '';
    const isEthnicGroupValid = ethnicGroups.length > 0;
    const isEthnicGroupOtherValid = !ethnicGroups.includes('other') || ethnicGroupOther.trim() !== '';
    const isGenderValid = gender.trim() !== '';
    const isEmailFormatValid = EmailPattern.test(email);
    const isConfirmEmailSame = email === confirmEmail;
    const isPasswordValid = PasswordPattern.test(password);
    const isConfirmPasswordSame = password === confirmPassword;
    const isHowFindOutValid = howDidYouFindOut.trim() !== '';
    const isHowFindOutOtherValid = !howDidYouFindOut.includes('other') || howDidYouFindOutOther.trim() !== '';
    const isAgreeHelpValid = agreeHelp;
    const isAgreeTermsValid = agreeTermsConditions;
    const isCaptchaValid = window.location.origin.indexOf("http://localhost:3000") === 0 ? true : captchaValue !== null;

    const errors = {
      username: !isUsernameValid ? ErrorEnum.INVALID_USERNAME : (!isUsernameFormatValid ? ErrorEnum.INVALID_USERNAME_FORMAT : ''),
      ageGroup: !isAgeGroupValid ? ErrorEnum.INVALID_AGE_GROUP : '',
      ethnicGroups: !isEthnicGroupValid ? ErrorEnum.INVALID_ETHNIC_GROUP : '',
      ethnicGroupOther: !isEthnicGroupOtherValid ? ErrorEnum.INVALID_OTHER_ETHNIC : '',
      gender: !isGenderValid ? ErrorEnum.INVALID_GENDER : '',
      email: !isEmailFormatValid ? ErrorEnum.INVALID_EMAIL_FORMAT : '',
      confirm_email: !isConfirmEmailSame ? ErrorEnum.INVALID_CONFIRM_EMAIL : '',
      password: password.trim().length < 8 ? ErrorEnum.INVALID_PASSWORD : (!isPasswordValid ? ErrorEnum.INVALID_PASSWORD_FORMAT : ''),
      confirm_password: !isConfirmPasswordSame ? ErrorEnum.INVALID_CONFIRM_PASSWORD : '',
      howDidYouFindOut: !isHowFindOutValid ? ErrorEnum.INVALID_HOW_DID_YOU_FIND_OUT : '',
      howDidYouFindOutOther: !isHowFindOutOtherValid ? ErrorEnum.INVALID_OTHER_HOW_DID_YOU_FIND_OUT : '',
      agreement_extra_help: !isAgreeHelpValid ? ErrorEnum.INVALID_AGREEMENT_EXTRA_HELP : '',
      agreement_terms: !isAgreeTermsValid ? ErrorEnum.INVALID_AGREEMENT_TERMS : '',
      captcha: !isCaptchaValid ? ErrorEnum.INVALID_CAPTCHA : '',
    };

    // Only update errorMessages state if the errors have changed
    if (JSON.stringify(errors) !== JSON.stringify(errorMessages)) {
      setErrorMessages(errors);
    }

    return (
      isUsernameValid && isUsernameFormatValid &&
      isAgeGroupValid &&
      isEthnicGroupValid && isEthnicGroupOtherValid &&
      isGenderValid &&
      isEmailFormatValid && isConfirmEmailSame &&
      isPasswordValid && isConfirmPasswordSame &&
      isHowFindOutValid && isHowFindOutOtherValid &&
      isAgreeHelpValid && isAgreeTermsValid &&
      isCaptchaValid
    );
  }

  const handleSubmit = async (event: any) => {
    event.preventDefault();

    setIsFormTouchedAfterFailedSubmit(false);

    if (!checkFormValid()) {
      setIsBannerOpen(true);
      scrollToElement('RegisterBanner');
      return;
    }

    try {
      setIsSubmitting(true);

      await registerSPARXUser({
        username,
        ageGroup,
        ethnicGroups,
        ...(ethnicGroups.includes("other") && ethnicGroupOther && { ethnicGroupOther }), // Conditionally add ethnicGroupOther
        gender,
        email,
        password,
        howDidYouFindOut,
        ...(howDidYouFindOut.includes("other") && howDidYouFindOutOther && { howDidYouFindOutOther }), // Conditionally add howDidYouFindOutOther
        emailAllow: true,
      });
      setIsSubmitting(false);

      navigate(`/activate?username=${encodeURIComponent(username)}`);
    } catch (error: any) {
      setIsSubmitting(false);
      // message for username and email exists error
      const errorMessage = error.message;
      const isUsernameError = errorMessage.includes('Username already exists');
      const isEmailError = errorMessage.includes('Email already exists');

      const formattedError = isUsernameError
        ? ErrorEnum.USERNAME_IN_USE.replace("{PLACEHOLDER}", username)
        : isEmailError
          ? ErrorEnum.EMAIL_IN_USE.replace("{PLACEHOLDER}", email)
          : errorMessage;

      setBackendErrorMessages(formattedError);
      setIsBannerOpen(true);
      scrollToElement('RegisterBanner');
    }
  }

  const scrollToElement = (elementId: string) => {
    const targetElement = document.getElementById(elementId);
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      targetElement.focus();
    } else {
      console.error('Element not found');
    }
  }

  const errorMessagesArray = [
    backendErrorMessages,
    errorMessages.username,
    errorMessages.ageGroup,
    errorMessages.ethnicGroups,
    errorMessages.ethnicGroupOther,
    errorMessages.gender,
    errorMessages.email,
    errorMessages.confirm_email,
    errorMessages.password,
    errorMessages.confirm_password,
    errorMessages.howDidYouFindOut,
    errorMessages.howDidYouFindOutOther,
    errorMessages.agreement_extra_help,
    errorMessages.agreement_terms,
    errorMessages.captcha,
  ];

  return (
    <div data-testid="register" className={styles.Register}>
      <SpinnerModal open={isSubmitting} />

      <section id="RegisterTitle" className={styles.RegistrationTitle}>
        <Typography variant="h1">Registration</Typography>
      </section>

      <span id="RegisterBanner">
        {isBannerOpen && <div className={styles.RegisterBanner}>
          <div style={{display: 'flex', flexDirection: 'column'}}>
            {
              isIPBlocked ? <>
                  <div>Please note SPARX is currently only available to New Zealand residents.</div>
                  <div>If you would like information regarding availability of SPARX, please see our <a href="https://www.sparx.org.nz/faqs-technical-support" style={{color: '#843534', fontWeight: '500'}}>Technical Support FAQ</a> or contact <a href="mailto:support@sparx.org.nz" style={{color: '#843534', fontWeight: '500'}}>support@sparx.org.nz</a>.</div>
                </> :
                errorMessagesArray.map((message, index) => (
                  <React.Fragment key={index}>
                    <div dangerouslySetInnerHTML={{__html: message}}/>
                  </React.Fragment>
                ))
            }
          </div>
          <CloseIcon onClick={() => setIsBannerOpen(false)}/>
        </div>
        }
      </span>

      <Grid container>
        {/* Left Side - Form */}
        <Grid item xs={12} md={7} sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 4
        }}>
          <Box component="form" noValidate
               onSubmit={handleSubmit}
               onFocus={handleFormFocus}
               sx={{width: '96%', maxWidth: 648,
                 '@media (min-width: 900px)': {
                   width: '80%'
                 }
          }}>
            <Typography variant="body1" style={{ fontWeight: 500 }}>
              Please fill out all the fields before submitting.
            </Typography>
            <br />

            {/* Username Field */}
            <Box sx={{ marginBottom: 5, color: errorMessages.username ? 'error.main' : 'text.primary' }}>
              <Typography variant="subtitle2" color="inherit" gutterBottom>Username*</Typography>
              <Input disabled={isIPBlocked} autoFocus fullWidth disableUnderline placeholder="Type your username here..."
                     sx={{ borderColor: errorMessages.username ? 'error.main' : '#E6E8F3' }}
                     value={username} onChange={(e) => setUsername(e.target.value)}/>
              <Typography variant="caption" color="inherit">We recommend you do not use your real name.</Typography>
            </Box>

            {/* Who are you Field */}
            <Box sx={{ marginBottom: 5, color: errorMessages.ageGroup ? 'error.main' : 'text.primary' }}>
              <Typography variant="subtitle2" color="inherit" gutterBottom>Who are you?*</Typography>
              <FormControl fullWidth>
                <Select disabled={isIPBlocked} sx={{ borderColor: errorMessages.ageGroup ? 'error.main' : '#E6E8F3' }}
                        value={ageGroup} onChange={(e) => setAgeGroup(e.target.value as string)} displayEmpty inputProps={{ 'aria-label': 'WhoAreYou' }}>
                  <MenuItem value="" disabled>Please Select</MenuItem>
                  {whoAreYouGroupOptions.map((whoAreYouOption: any, index) => (
                    <MenuItem key={index} value={whoAreYouOption.value}>{whoAreYouOption.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="inherit">While it has been proven that SPARX benefits young people aged 13 to 19 years, anybody can use it.</Typography>
            </Box>

            {/* Ethnic Groups Field */}
            <Box sx={{ marginBottom: 5, color: errorMessages.ethnicGroups ? 'error.main' : 'text.primary' }}>
              <Typography variant="subtitle2" color="inherit" gutterBottom>What are your main ethnic groups? (Tick all that apply)*</Typography>
              <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, marginBottom: 4}}>
                {ethnicGroupOptions.map(group => (
                  <FormControlLabel key={group.value} label={group.label}
                    control={<Checkbox disabled={isIPBlocked} checked={ethnicGroups.includes(group.value)} onChange={handleEthnicCheckboxChange} name={group.value}/>}
                  />
                ))}
              </Box>
              {ethnicGroups.includes('other') && (
                <>
                  <Typography variant="subtitle2" gutterBottom sx={{ color: errorMessages.ethnicGroupOther ? 'error.main' : 'text.primary' }}>
                    If Other, which ethnic group?*
                  </Typography>
                  <Input disabled={isIPBlocked} autoFocus fullWidth disableUnderline placeholder=""
                         sx={{ borderColor: errorMessages.ethnicGroupOther ? 'error.main' : '#E6E8F3' }}
                         value={ethnicGroupOther} onChange={(e) => setEthnicGroupOther(e.target.value)}/>
                </>
              )}
            </Box>

            {/* Gender Field */}
            <Box sx={{ marginBottom: 5, color: errorMessages.gender ? 'error.main' : 'text.primary' }}>
              <Typography variant="subtitle2" color="inherit" gutterBottom>What is your gender?*</Typography>
              <FormControl fullWidth>
                <Select disabled={isIPBlocked} sx={{borderColor: errorMessages.gender ? 'error.main' : '#E6E8F3' }}
                        value={gender} onChange={(e) => setGender(e.target.value as string)} displayEmpty inputProps={{ 'aria-label': 'Gender' }}>
                  <MenuItem value="" disabled>Please Select</MenuItem>
                  {genderGroupOptions.map((genderOption: any, index) => (
                    <MenuItem key={index} value={genderOption.value}>{genderOption.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Emails */}
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 0, marginBottom: 5,
              '@media (min-width: 800px)': {
                flexDirection: 'row',
              }
            }}>
              {/* Email Field */}
              <Box sx={{ flex: '1', color: errorMessages.email ? 'error.main' : 'text.primary' }}>
                <Typography variant="subtitle2" color="inherit" gutterBottom>Email address*</Typography>
                <Input disabled={isIPBlocked} autoFocus fullWidth disableUnderline placeholder="Type your email here..."
                       sx={{ borderColor: errorMessages.email ? 'error.main' : '#E6E8F3' }}
                       value={email} onChange={(e) => setEmail(e.target.value)}/>
                <Typography variant="caption" color="inherit">For resetting your password.</Typography>
              </Box>

              <div style={{width: 'clamp(15px, 7.03vw - 41.2px, 60px)', height: '10px'}}></div>

              {/* Confirm Email Field */}
              <Box sx={{ flex: '1', color: errorMessages.confirm_email ? 'error.main' : 'text.primary' }}>
                <Typography variant="subtitle2" color="inherit" gutterBottom>Confirm Email</Typography>
                <Input disabled={isIPBlocked} autoFocus fullWidth disableUnderline placeholder="Retype your email here..."
                       sx={{ borderColor: errorMessages.confirm_email ? 'error.main' : '#E6E8F3' }}
                       value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)}/>
              </Box>
            </Box>

            {/* Passwords */}
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 0, marginBottom: 5,
              '@media (min-width: 800px)': {
                flexDirection: 'row',
              }
            }}>
              {/* Password Field */}
              <Box sx={{ flex: '1', color: errorMessages.password ? 'error.main' : 'text.primary' }}>
                <Typography variant="subtitle2" color="inherit" gutterBottom>Password*</Typography>
                <Input disabled={isIPBlocked} type="password" fullWidth disableUnderline placeholder=""
                       sx={{ marginBottom: 1, borderColor: errorMessages.password ? 'error.main' : '#E6E8F3' }}
                       value={password} onChange={(e) => setPassword(e.target.value)}/>
                <Typography variant="caption" color="inherit">Password strength: {passwordStrength}</Typography>
                {password && (passwordRecommendations.filter(rec => !rec.valid).length > 0) && (
                  <Box sx={{ marginTop: 0 }}>
                    <Typography variant="caption" color="inherit" gutterBottom>Recommendations to make your password stronger:</Typography>
                    <ul>
                      {passwordRecommendations.map((rec, index) => (
                        !rec.valid && <li key={index}>{rec.message}</li>
                      ))}
                    </ul>
                  </Box>
                )}
              </Box>

              <div style={{width: 'clamp(15px, 7.03vw - 41.2px, 60px)', height: '10px'}}></div>

              {/* Confirm Password Field */}
              <Box sx={{ flex: '1', color: errorMessages.confirm_password ? 'error.main' : 'text.primary' }}>
                <Typography variant="subtitle2" color="inherit" gutterBottom>Confirm password*</Typography>
                <Input disabled={isIPBlocked} type="password" fullWidth disableUnderline placeholder=""
                       sx={{ marginBottom: 1, borderColor: errorMessages.confirm_password ? 'error.main' : '#E6E8F3' }}
                       value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}/>
                <Typography variant="caption" color="inherit">
                  Password match: { confirmPassword.length === 0 ? '' : (confirmPassword === password ? 'yes' : 'no') }
                </Typography>
              </Box>
            </Box>

            {/* How find us Field */}
            <Box sx={{ marginBottom: 5, color: errorMessages.howDidYouFindOut ? 'error.main' : 'text.primary' }}>
              <Typography variant="subtitle2" color="inherit" gutterBottom>How did you find out about SPARX?*</Typography>
              <FormControl fullWidth>
                <Select disabled={isIPBlocked} sx={{borderColor: errorMessages.howDidYouFindOut ? 'error.main' : '#E6E8F3' }}
                        value={howDidYouFindOut} onChange={(e) => setHowDidYouFindOut(e.target.value as string)} displayEmpty inputProps={{ 'aria-label': 'How did you find out' }}>
                  <MenuItem value="" disabled>Please Select</MenuItem>
                  {howDidYouFindOutOptions.map((findOption: any, index) => (
                    <MenuItem key={index} value={findOption.value}>{findOption.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {howDidYouFindOut.includes('other') && (
                <>
                  <br/><br/>
                  <Typography variant="subtitle2" sx={{color: errorMessages.howDidYouFindOutOther ? 'error.main' : 'text.primary' }} gutterBottom>If Other, please specify:*</Typography>
                  <Input disabled={isIPBlocked} autoFocus fullWidth disableUnderline placeholder=""
                         sx={{borderColor: errorMessages.howDidYouFindOutOther ? 'error.main' : '#E6E8F3' }}
                         value={howDidYouFindOutOther} onChange={(e) => setHowDidYouFindOutOther(e.target.value)}/>
                </>
              )}
            </Box>

            {/* Agreements */}
            <Box sx={{ marginTop: 4 }}>
              <FormControlLabel
                control={<Checkbox sx={{color: errorMessages.agreement_extra_help ? 'error.main' : 'text.primary' }} disabled={isIPBlocked} checked={agreeHelp} onChange={(e) => setAgreeHelp(e.target.checked)}/>}
                label={<span className={styles.RegisterAgreementLabel} style={{color: errorMessages.agreement_extra_help ? '#f44336' : '#04193B'}}>I agree to <a href="https://www.sparx.org.nz/contact" target="_blank" rel="noopener noreferrer" style={{color: '#2D94E8'}}>get extra help</a> if SPARX is not helping me.</span>}
              />
              <FormControlLabel
                control={<Checkbox sx={{color: errorMessages.agreement_terms ? 'error.main' : 'text.primary' }} disabled={isIPBlocked} checked={agreeTermsConditions} onChange={(e) => setAgreeTermsConditions(e.target.checked)}/>}
                label={<span className={styles.RegisterAgreementLabel} style={{color: errorMessages.agreement_terms ? '#f44336' : '#04193B'}}>I agree to the <a href="https://www.sparx.org.nz/terms-conditions" target="_blank" rel="noopener noreferrer" style={{color: '#2D94E8'}}>Terms and Conditions</a>*</span>}
              />
            </Box>

            {/* Submit */}
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, marginTop: 3, marginBottom: 8,
              '@media (min-width: 800px)': {
                flexDirection: 'row',
              }
            }}>
              {/* Google ReCAPTCHA */}
              <Box sx={{ flex: '1' }}>
                <ReCAPTCHA
                  sitekey={RECAPTCHA_SITE_KEY}
                  onChange={(value: string | null) => setCaptchaValue(value)}
                />
              </Box>

              <div></div>

              {/* submit button */}
              <Box sx={{ flex: '1' }}>
                <button className={`PrimaryButton ${styles.RegisterButton}`} disabled={isSubmitting}>SUBMIT</button>
              </Box>
            </Box>

            {isSubmitting && <Typography variant="h2" style={{color: 'black'}}>Submitting, please wait...</Typography>}
          </Box>
        </Grid>

        {/* Right Side - Image */}
        <Grid item xs={false} md={5}
              sx={{
                width: '40%',
                height: 'auto',
                backgroundImage: `url(${backgroundImage})`,
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'contain',
                backgroundPositionX: 'right'
              }}
        />
      </Grid>
    </div>
  );
};

export default Register;
